import React, { useCallback, useEffect, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import toast from "react-hot-toast";
import { ChevronDown, ChevronUp, Info, Trash2, Upload } from "lucide-react";
import { db, functions, storage } from "../firebase";
import { compressImage, formatBytes } from "../utils/compress-image";
// Same file the bake script reads, so the picker can never show an icon the
// live page doesn't have.
import BONUS_ICONS from "../utils/bonus-icons.json";
import {
  parseBonusValue,
  formatBonusMoney,
  totalBonusValue,
} from "../utils/bonus-value";
import {
  bonusTitleText,
  canStrikeBonus,
  isBonusStruck,
  setBonusTitleStruck,
} from "../utils/bonus-markdown";
/* THE registry. Everything country-shaped on this screen — the tabs, the
 * currency prefix, the gateway line, the Firestore path, every ×100 — comes
 * from here, so adding Thailand is still the one-line change functions/
 * countries.js promises. There is deliberately no country list in this file.
 *
 * src/utils/countries.js is a SYMLINK to functions/countries.js, not a copy.
 * CRA's ModuleScopePlugin refuses `../../functions/...` outright ("falls outside
 * of the project src/ directory"), and a copy is exactly the drift the registry
 * exists to prevent — so the file is linked in rather than duplicated. Editing
 * either path edits the one file. */
import {
  COUNTRIES,
  DAYS,
  docFor,
  formatAmount,
  gatewayFor,
  toMajor,
  toMinor,
} from "../utils/countries";

/* Multi-region editor for the checkout v2 pages.
 *
 * The shape of the screen follows the split in the data: PRICE and PROMO CODES
 * are per country and sit under a country tab; TITLE, HERO, BONUSES and SEATS
 * are one set of fields shared by every country. Bonus values are USD figures
 * that are identical in every market, so a per-country copy of them would only
 * ever be a way for Singapore's bonus stack to quietly fall a version behind
 * Malaysia's.
 *
 * Firestore still stores a full config per (day, country) — the bake script
 * reads one doc per variant page — so "shared" is enforced on the way out:
 * publishing writes the same content to every country's doc, each with its own
 * amount and codes. The country tabs are a view over one form, not four forms;
 * switching tabs keeps everything you have typed.
 *
 * CURRENCY AND GATEWAY ARE NEVER FIELDS. They are printed as context and come
 * from the registry. An admin who could edit them could point a country's money
 * at the wrong processor, which is not a mistake any amount of confirmation
 * dialog makes safe.
 *
 * As in v1: publishing writes Firestore and triggers a Netlify rebuild, so the
 * page copy takes ~2 minutes, while the price charged is read from Firestore on
 * every order and applies immediately. */

/* Flag from the ISO code: each letter maps to its regional-indicator symbol.
 * Derived rather than stored so the registry never grows a `flag` field that
 * somebody forgets to fill in when they add a market — the code IS the flag. */
export function flagOf(code) {
  return String(code)
    .toUpperCase()
    .replace(/[A-Z]/g, (ch) =>
      String.fromCodePoint(127397 + ch.charCodeAt(0))
    );
}

/* Where the generated variant will be served from — §2 of the plan. Built here
 * rather than stored in the registry because it is a fact about the bake
 * script's output layout, not about the country. */
export function pageUrl(day, code) {
  return `/checkout-v2-day${day}/${code}/`;
}

/* Method codes -> what an admin calls them. Keyed by METHOD, not by country, so
 * a new market that reuses existing rails needs no entry; an unknown rail falls
 * through to its raw code rather than vanishing from the context line. */
const METHOD_LABELS = {
  card: "Card",
  fpx: "FPX",
  wallet: "E-wallet",
  paynow: "PayNow",
};

const titleCase = (s) => String(s || "").replace(/^./, (c) => c.toUpperCase());

/* "HitPay (card) · Curlec (FPX, e-wallet)" — the processors a country's tabs
 * actually reach, grouped by processor. A country whose tabs all share one
 * processor reads as plain "HitPay". Resolved through gatewayFor so this line
 * can never disagree with what createOrderV2 charges through. */
const GATEWAY_NAMES = { curlec: "Curlec", stripe: "Stripe", hitpay: "HitPay" };
function gatewayLine(country) {
  const groups = new Map();
  (country.methods || ["card"]).forEach((m) => {
    const g = gatewayFor(country, m);
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(METHOD_LABELS[m] || m);
  });
  const name = (g) => GATEWAY_NAMES[g] || titleCase(g);
  if (groups.size === 1) return name([...groups.keys()][0]);
  return [...groups.entries()]
    .map(([g, ms]) => `${name(g)} (${ms.join(", ")})`)
    .join(" · ");
}

/* Decimal places for a currency, read off minorFactor rather than assumed to be
 * two — the same computation toHitPayAmount() makes. IDR arrives with
 * minorFactor 1 and must render "1500000", not "1500000.00". */
const placesFor = (country) =>
  country.minorFactor === 1 ? 0 : String(country.minorFactor).length - 1;

/* Minor units (stored) -> the string in the input box (major units).
 * Anything that isn't a whole number of minor units is treated as "not set":
 * a half-written doc should show an empty box, not "NaN". */
function minorToInput(minor, country) {
  if (!Number.isInteger(minor)) return "";
  return toMajor(minor, country).toFixed(placesFor(country));
}

/* The input box -> a major-unit Number, or null when it is blank.
 *
 * Strips everything that isn't a digit, a dot or a minus first. The plan says
 * an admin types "3,998" and they also paste "RM 3,998.00" out of a chat; both
 * are Number("...") === NaN, and a price that silently fails to parse is the
 * worst possible failure here. */
function parseMajorInput(text) {
  const cleaned = String(text == null ? "" : text).replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

/* Major-unit input -> integer minor units, via the registry's toMinor so the
 * admin and createOrderV2 cannot disagree about what 3,998 means. Returns null
 * for a blank box and NaN for something unparseable, which the caller has to
 * tell apart: blank is "this country isn't priced yet", NaN is a typo. */
function inputToMinor(text, country) {
  const major = parseMajorInput(text);
  if (major === null) return null;
  if (!Number.isFinite(major)) return NaN;
  return toMinor(major, country);
}

/* The discount the buyer sees, to one decimal place.
 *
 * One decimal on purpose. Australia is 32.2% where the others are ~33.3%, and
 * that is a deliberate price point, not a rounding artefact — a whole-number
 * "32%" next to "33%" reads as sloppiness and invites someone to "fix" it. */
function discountPct(compareMinor, amountMinor) {
  if (!Number.isInteger(compareMinor) || !Number.isInteger(amountMinor)) return null;
  if (compareMinor <= 0 || amountMinor > compareMinor) return null;
  return ((compareMinor - amountMinor) / compareMinor) * 100;
}

/* Stable JSON so two configs can be compared for equality regardless of the
 * order keys happen to have been built in — used to decide which country docs a
 * publish actually needs to touch. */
function stable(value) {
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stable(value[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
}

const ICON_KEYS = Object.keys(BONUS_ICONS);

/* Renders one icon from the shared set. The markup is a developer-authored
   constant in bonus-icons.json, never user input, so injecting it is safe. */
function BonusIcon({ name, size = 18 }) {
  const paths = BONUS_ICONS[name];
  if (!paths) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: paths }}
    />
  );
}

/* Click the swatch, pick from a grid of the actual icons — a name-only dropdown
   made you guess what "blueprint" looked like and then publish to find out. */
function IconPicker({ value, onChange }) {
  return (
    <div className="dropdown">
      <button
        tabIndex={0}
        type="button"
        className="btn btn-sm btn-outline w-14"
        aria-label={`Icon: ${value || "none"}`}
      >
        {value ? <BonusIcon name={value} /> : <span className="opacity-40">—</span>}
      </button>
      {/* Explicit width: dropdown-content is absolutely positioned and shrinks
          to fit, which collapses the grid into a single narrow column. */}
      <div
        tabIndex={0}
        className="dropdown-content z-[1] mt-1 w-56 p-2 bg-base-100 rounded-box shadow-lg border border-base-300"
      >
        <div className="grid grid-cols-5 gap-1">
          <button
            type="button"
            className={`flex h-9 w-9 items-center justify-center rounded hover:bg-base-200 ${
              !value ? "bg-primary/15 text-primary" : ""
            }`}
            onClick={() => onChange("")}
            title="No icon"
          >
            <span className="opacity-40">—</span>
          </button>
          {ICON_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={`flex h-9 w-9 items-center justify-center rounded hover:bg-base-200 ${
                value === key ? "bg-primary/15 text-primary" : ""
              }`}
              onClick={() => onChange(key)}
              title={key}
            >
              <BonusIcon name={key} size={20} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Two short fields per line on a phone: half the width, less half the gap-2. */
const HALF_ON_MOBILE = "w-[calc(50%-0.25rem)]";

/* One field inside a repeated row. On a laptop a field's position says which
   one it is, so the label is hidden; stacked on a phone that cue is gone and an
   unlabelled column of boxes is unfillable, so it shows. */
function RowField({ label, className = "", children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[10px] uppercase tracking-wide opacity-50 sm:hidden">
        {label}
      </span>
      {children}
    </div>
  );
}

/* Bonus rows are reorderable, so React needs a key that travels with the row
   rather than one that describes where it currently sits. Local to the form —
   handlePublish writes the stored fields out by name, so it never reaches
   Firestore. */
let bonusUid = 0;
const nextBonusId = () => `v2b${++bonusUid}`;

const EMPTY_BONUS = {
  icon: "",
  name: "",
  nameZh: "",
  tag: "",
  tagZh: "",
  value: "",
  valueZh: "",
};

const EMPTY_PRICE = { amount: "", compareAmount: "", promoCodes: [] };

const EMPTY_CONTENT = {
  title: "",
  titleZh: "",
  images: [],
  bonuses: [],
  // Not editable — every value renders with a "$" and the total uses the same
  // helper as the rows. Still carried so the doc shape is unchanged and the
  // server's validator keeps seeing the field it expects.
  bonusCurrency: "USD",
  seatsStart: "",
  seatsFloor: "3",
};

/* Firestore doc -> the shared half of the form. Only ever called on the one doc
   chosen as canonical below, so this cannot mix two countries' copy. */
function contentFrom(d) {
  return {
    title: d.title || "",
    titleZh: d.titleZh || "",
    // Older configs stored bare URL strings; normalise so the rest of the
    // component only deals with one shape.
    images: (Array.isArray(d.images) ? d.images : []).map((img) =>
      typeof img === "string" ? { url: img, width: null, height: null } : img
    ),
    // Older configs stored bonuses as plain name strings.
    bonuses: (Array.isArray(d.bonuses) ? d.bonuses : []).map((b) =>
      typeof b === "string"
        ? { ...EMPTY_BONUS, _id: nextBonusId(), name: b }
        : {
            _id: nextBonusId(),
            icon: b.icon || "",
            name: b.name || "",
            nameZh: b.nameZh || "",
            tag: b.tag || "",
            tagZh: b.tagZh || "",
            // Stored as a number or a label; the field edits both as text.
            value: b.value == null ? "" : String(b.value),
            valueZh: b.valueZh || "",
          }
    ),
    bonusCurrency: d.bonusCurrency || "USD",
    seatsStart: d.seats?.start ? String(d.seats.start) : "",
    seatsFloor: d.seats?.floor != null ? String(d.seats.floor) : "3",
  };
}

/* Firestore doc -> the per-country half. Every money field goes through the
   registry, so a zero-decimal currency renders with no decimal part instead of
   gaining two that would be multiplied back in on publish.
   `lockedPromoCodes` is the checkout_promos read (null if there was none) —
   it wins over `d.promoCodes` when present, mirroring loadConfig's own
   precedence in create-order-v2.js. site_config no longer carries the field
   at all once a country has published through this screen, since publish
   deletes it there; `d.promoCodes` only ever matters for a country still
   mid-migration. */
function priceFrom(d, country, lockedPromoCodes) {
  const codes = lockedPromoCodes && typeof lockedPromoCodes === "object"
    ? lockedPromoCodes
    : d.promoCodes || {};
  return {
    amount: minorToInput(d.amount, country),
    compareAmount: minorToInput(d.compareAmount, country),
    promoCodes: Object.entries(codes).map(([code, p]) => ({
      code,
      type: p.type === "percent" ? "percent" : "flat",
      value: p.type === "percent" ? String(p.value) : minorToInput(p.value, country),
      expires: p.expires ? String(p.expires).slice(0, 10) : "",
    })),
  };
}

/* The payload for one country: shared content + that country's money. This is
   the ONLY place a config is assembled, so the dirty check below can compare
   what a publish would send against what the load produced by running the same
   function over both. */
function buildConfig(form, country) {
  const price = form.byCountry[country.code] || EMPTY_PRICE;
  const promoCodes = {};
  for (const p of price.promoCodes) {
    if (!p.code.trim()) continue;
    promoCodes[p.code.trim().toUpperCase()] = {
      type: p.type,
      value:
        p.type === "percent"
          ? Number(parseMajorInput(p.value) ?? 0)
          : inputToMinor(p.value, country),
      expires: p.expires || null,
    };
  }

  return {
    title: form.title.trim(),
    titleZh: form.titleZh.trim(),
    amount: inputToMinor(price.amount, country),
    // null, not 0 or absent: "no struck-through price" has to be expressible,
    // and the page decides whether to draw the compare row from exactly this.
    compareAmount: price.compareAmount.trim()
      ? inputToMinor(price.compareAmount, country)
      : null,
    images: form.images,
    bonuses: form.bonuses
      .filter((b) => b.name.trim())
      .map((b) => ({
        icon: b.icon.trim(),
        // Titles publish with their strike markers normalised: both languages
        // carry them or neither does. The page strikes the whole row off
        // either title, so a half-marked pair would leave the config saying
        // something the rendered page doesn't.
        name: setBonusTitleStruck(b.name, isBonusStruck(b)) || b.name.trim(),
        nameZh: setBonusTitleStruck(b.nameZh, isBonusStruck(b)),
        tag: b.tag.trim(),
        tagZh: (b.tagZh || "").trim(),
        // Sent as typed. Deciding number-vs-label here as well as on the server
        // would be two places to disagree about what a buyer sees.
        value: String(b.value ?? "").trim(),
        valueZh: (b.valueZh || "").trim(),
      })),
    bonusCurrency: form.bonusCurrency.trim() || "USD",
    promoCodes,
    seats: form.seatsStart
      ? { start: Number(form.seatsStart), floor: Number(form.seatsFloor || 3) }
      : null,
  };
}

/* `day` and `country` are owned by the page so its tab rows and Publish button
 * can sit in the header. This component reads them, hands its publish action up
 * through `publishRef`, and reports its own status so those header controls can
 * disable themselves — they are no longer close enough to see a publish in
 * flight for themselves. Same contract as CheckoutEditor. */
export default function CheckoutV2Editor({
  token,
  day = 1,
  country = COUNTRIES[0].code,
  onStatusChange,
  publishRef,
}) {
  const [form, setForm] = useState(() => ({
    ...EMPTY_CONTENT,
    byCountry: Object.fromEntries(COUNTRIES.map((c) => [c.code, EMPTY_PRICE])),
  }));
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Which country docs already exist. A country whose doc has never been seeded
  // is a legitimate state — the registry can carry a market before anyone has
  // priced it — and it changes what publish does, so it is tracked rather than
  // inferred from a blank price.
  const [existing, setExisting] = useState({});
  const fileInput = useRef(null);
  /* stable(buildConfig(...)) per country, as loaded. A publish only writes the
     docs whose payload actually changed. That is not an optimisation: Malaysia
     shares its doc with the live /checkout/ page, so re-writing it because
     somebody edited Singapore's price would put an unrelated write on the one
     document a webinar is currently selling from. */
  const baseline = useRef({});

  const active = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];
  const activePrice = form.byCountry[active.code] || EMPTY_PRICE;
  const docPath = docFor(day, active.code);
  /* True when the registry PINS this country's doc instead of using the
     checkout_v2_day{N}_{code} convention — which today means Malaysia, sharing
     the document /checkout/ already reads. Derived from the registry rather
     than written as `code === "my"`, so the notice follows the data if another
     market is ever pinned the same way. */
  const sharesExistingDoc = Boolean(active.docs && active.docs[day]);

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  /* Patches one country's money without touching the others — the tabs are a
     view over a single form, so switching country must never be a save point. */
  const setPrice = useCallback((code, patch) => {
    setForm((f) => ({
      ...f,
      byCountry: {
        ...f.byCountry,
        [code]: { ...(f.byCountry[code] || EMPTY_PRICE), ...patch },
      },
    }));
  }, []);

  const updateBonus = useCallback((index, patch) => {
    setForm((f) => ({
      ...f,
      bonuses: f.bonuses.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    }));
  }, []);

  const moveBonus = useCallback((from, to) => {
    setForm((f) => {
      if (from === to || to < 0 || to >= f.bonuses.length) return f;
      const next = f.bonuses.slice();
      next.splice(to, 0, next.splice(from, 1)[0]);
      return { ...f, bonuses: next };
    });
  }, []);

  const updatePromo = useCallback(
    (code, index, patch) => {
      setForm((f) => {
        const price = f.byCountry[code] || EMPTY_PRICE;
        return {
          ...f,
          byCountry: {
            ...f.byCountry,
            [code]: {
              ...price,
              promoCodes: price.promoCodes.map((p, i) =>
                i === index ? { ...p, ...patch } : p
              ),
            },
          },
        };
      });
    },
    []
  );

  // Keeps the page's tab rows and Publish button honest: the day switch locks
  // while a publish is in flight so a stray click cannot swap the form out from
  // under the request, and Publish also waits out the initial load.
  useEffect(() => {
    if (onStatusChange) onStatusChange({ publishing, loading });
  }, [publishing, loading, onStatusChange]);

  /* Loads every country for the day in one pass.
   *
   * All of them, not just the visible tab, because the country tabs are a view
   * over one form and publish writes every changed doc — a tab that hadn't been
   * visited would otherwise publish as blank.
   *
   * Reloads on a day switch and deliberately drops unpublished edits rather
   * than carrying them across: silently moving half-typed day 1 copy onto
   * day 2's form is how the wrong price gets published. Switching COUNTRY does
   * not reload, because nothing about the country changes what is loaded.
   * Also reloads if `token` changes (a re-verify) — it's in the dependency
   * array only because getCheckoutV2Promos needs a current one, not because a
   * fresh token should ever discard unpublished edits by itself. */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // The config docs are world-readable and come straight off the client
        // SDK; the promo codes are not — checkout_promos denies every client
        // read by rule (see publish-checkout-v2.js) — so they come back
        // through a callable that reads with the Admin SDK instead. A failure
        // here (e.g. a stale admin token) is not fatal to loading the form: it
        // falls back to whatever promoCodes each config doc happens to carry,
        // same as before this callable existed.
        const getPromos = httpsCallable(functions, "getCheckoutV2Promos");
        const [snaps, promosRes] = await Promise.all([
          Promise.all(COUNTRIES.map((c) => getDoc(doc(db, docFor(day, c.code))))),
          getPromos({ token, day }).catch(() => null),
        ]);
        if (cancelled) return;

        const promosByCountry = (promosRes?.data?.ok && promosRes.data.promos) || {};

        const exists = {};
        COUNTRIES.forEach((c, i) => {
          exists[c.code] = snaps[i].exists();
        });

        /* Content is shared, so exactly one doc gets to be its source: the
           first country in registry order that has been published. Malaysia is
           first and is the doc /checkout/ has been serving for months, so in
           practice the shared copy is whatever is live today. Taking it from
           the active tab instead would make the same field show different text
           depending on which country you happened to click. */
        const canonical = snaps.find((s) => s.exists());
        const next = {
          ...(canonical ? contentFrom(canonical.data()) : EMPTY_CONTENT),
          byCountry: Object.fromEntries(
            COUNTRIES.map((c, i) => [
              c.code,
              snaps[i].exists()
                ? priceFrom(snaps[i].data(), c, promosByCountry[c.code])
                : EMPTY_PRICE,
            ])
          ),
        };

        /* Baselines run the loaded form back through buildConfig, so the dirty
           check compares like with like — including the normalisations
           publishing applies (trimmed titles, tidied strike markers). A doc
           that has never existed has no baseline and always counts as changed. */
        baseline.current = {};
        COUNTRIES.forEach((c) => {
          baseline.current[c.code] = exists[c.code]
            ? stable(buildConfig(next, c))
            : null;
        });

        setExisting(exists);
        setForm(next);
      } catch (e) {
        if (!cancelled) {
          toast.error(`Could not load the Day ${day} checkout configs.`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [day, token]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await compressImage(file);
      const ext = result.type === "image/webp" ? "webp" : "jpg";
      // Same bucket prefix as v1: the hero is shared content, and while both
      // checkouts are live the same file is genuinely serving both.
      const path = `checkout/${Date.now()}-hero.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, result.blob, { contentType: result.type });
      const url = await getDownloadURL(storageRef);

      // Dimensions travel with the URL so the checkout page can reserve the
      // right space before the image loads — without them the price jumps down
      // as it arrives.
      setForm((f) => ({
        ...f,
        images: [{ url, width: result.width, height: result.height }, ...f.images].slice(0, 6),
      }));
      toast.success(
        result.compressed
          ? `Uploaded — ${formatBytes(result.originalBytes)} compressed to ${formatBytes(result.bytes)}`
          : `Uploaded (${formatBytes(result.bytes)}, already optimised)`
      );
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const handlePublish = async () => {
    if (!form.title.trim()) return toast.error("Give the product a title.");

    /* Work out which countries this publish touches before writing anything.
     *
     * A country is skipped when its price is blank AND it has no doc — the
     * registry is allowed to carry a market nobody has priced yet, and writing
     * a config with no amount would ship a page that cannot charge. A blank
     * price on a country that DOES have a doc is an error, not a skip: that one
     * is live, and publishing it empty would take its price away. */
    const targets = [];
    for (const c of COUNTRIES) {
      const price = form.byCountry[c.code] || EMPTY_PRICE;
      const amount = inputToMinor(price.amount, c);

      if (amount === null) {
        if (existing[c.code]) {
          return toast.error(
            `${c.label} has a published config but no sale price. Enter one, or the page loses its price.`
          );
        }
        continue; // never seeded, nothing to price yet
      }
      if (!Number.isInteger(amount) || amount <= 0) {
        return toast.error(
          `${c.label}: enter the sale price in ${c.currency}, e.g. ${minorToInput(
            125700,
            c
          )}`
        );
      }

      const compare = price.compareAmount.trim() ? inputToMinor(price.compareAmount, c) : null;
      if (compare !== null && (!Number.isInteger(compare) || compare <= 0)) {
        return toast.error(`${c.label}: the compare price isn't a number.`);
      }
      // Strictly above, matching validateCompareAmount server-side. Equal is
      // rejected there too: it renders a struck-through line next to "0% off",
      // which reads as a bug on the page. Caught here so the admin gets a
      // sentence instead of COMPARE_MUST_EXCEED_AMOUNT.
      if (compare !== null && compare <= amount) {
        return toast.error(
          `${c.label}: the compare price has to be above the sale price — otherwise there is no discount to show.`
        );
      }

      for (const p of price.promoCodes) {
        if (!p.code.trim()) continue;
        const label = p.code.trim().toUpperCase();
        if (p.type === "percent") {
          // Stricter than the server, which allows exactly 100. A free order
          // still has to reach a gateway that has a minimum, so a 100% code is
          // a typo every time it is entered here.
          const pct = parseMajorInput(p.value);
          if (pct === null || !Number.isFinite(pct) || pct <= 0 || pct >= 100) {
            return toast.error(`${c.label} · ${label}: a percentage between 0 and 100.`);
          }
        } else {
          const off = inputToMinor(p.value, c);
          if (off === null || !Number.isInteger(off) || off <= 0) {
            return toast.error(`${c.label} · ${label}: enter the amount off in ${c.currency}.`);
          }
          // A flat code worth more than the price would charge the floor
          // amount, which is never what anyone meant.
          if (off >= amount) {
            return toast.error(`${c.label} · ${label} is worth more than the price.`);
          }
        }
      }

      const config = buildConfig(form, c);
      // Unchanged docs are left alone — see the `baseline` ref.
      if (baseline.current[c.code] === stable(config)) continue;
      targets.push({ country: c, config });
    }

    if (!targets.length) {
      return toast("Nothing changed since this was loaded.", { icon: "👌" });
    }

    setPublishing(true);
    try {
      const publish = httpsCallable(functions, "publishCheckoutV2Config");
      const failed = [];
      let rebuilt = false;

      /* One call per changed country, sequentially. The callable takes a single
         country by contract, and going one at a time means a failure names the
         country it belongs to instead of arriving as an unlabelled rejected
         promise out of a Promise.all. */
      for (const t of targets) {
        try {
          const res = await publish({
            token,
            day,
            country: t.country.code,
            config: t.config,
          });
          const data = res.data || {};
          if (!data.ok) {
            failed.push(`${t.country.label}: ${data.error || "unknown error"}`);
          } else {
            // Each call may trigger its own rebuild; one is enough for the
            // whole site, so this only records whether any of them fired.
            if (data.rebuilt) rebuilt = true;
            baseline.current[t.country.code] = stable(t.config);
            setExisting((prev) => ({ ...prev, [t.country.code]: true }));
          }
        } catch (err) {
          failed.push(`${t.country.label}: ${err.message || "failed"}`);
        }
      }

      const done = targets.length - failed.length;
      if (failed.length) {
        // Partial success is reported as such. Silently succeeding on three of
        // four countries is how a market ends up a price behind.
        toast.error(
          `${done} of ${targets.length} published. Failed — ${failed.join("; ")}`,
          { duration: 10000 }
        );
      } else if (!rebuilt) {
        // Saved but the site didn't rebuild — prices are already live for new
        // orders, but the pages still show the old copy.
        toast(
          "Saved, but the site rebuild didn't trigger. Prices apply now; the pages will update on the next deploy.",
          { duration: 8000, icon: "⚠️" }
        );
      } else {
        toast.success(
          `Day ${day} published — ${targets
            .map((t) => t.country.code.toUpperCase())
            .join(", ")} live in ~2 minutes.`
        );
      }
    } finally {
      setPublishing(false);
    }
  };

  /* Hands the publish action to the page's header button. Reassigned on every
     render, deliberately without a dependency array, because handlePublish
     closes over the current form — a stale copy would publish whatever was
     typed one render ago. */
  useEffect(() => {
    if (!publishRef) return undefined;
    publishRef.current = handlePublish;
    return () => {
      publishRef.current = null;
    };
  });

  if (loading) {
    return (
      <div className="card bg-base-100 shadow">
        <div className="card-body gap-4 p-4 sm:p-8">
          <div className="flex items-center justify-center p-10">
            <span className="loading loading-spinner" />
          </div>
        </div>
      </div>
    );
  }

  const amountMinor = inputToMinor(activePrice.amount, active);
  const compareMinor = activePrice.compareAmount.trim()
    ? inputToMinor(activePrice.compareAmount, active)
    : null;
  const pct = discountPct(compareMinor, amountMinor);
  const inverted =
    Number.isInteger(compareMinor) &&
    Number.isInteger(amountMinor) &&
    compareMinor < amountMinor;

  return (
    <div className="card bg-base-100 shadow">
      <div className="card-body gap-4 p-4 sm:p-8">
        <div>
          <h2 className="card-title">
            {flagOf(active.code)} {active.label} — Day {day}
          </h2>

          {/* ---- Registry context. Read-only, by design. ---- */}
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-semibold">{active.currency}</span>
            <span className="opacity-40">·</span>
            <span className="font-semibold">{gatewayLine(active)}</span>
            <span className="opacity-40">·</span>
            <span className="opacity-80">
              {(active.methods || []).map((m) => METHOD_LABELS[m] || m).join(", ")}
            </span>
            {active.instalments && (
              <span className="badge badge-ghost badge-sm">instalments</span>
            )}
          </div>
          <p className="mt-1 text-xs opacity-60">
            Currency, gateway and payment methods come from{" "}
            <code className="px-1">functions/countries.js</code> and can’t be
            edited here — they decide which processor takes the money. Writing to{" "}
            <code className="px-1">{docPath}</code>.
          </p>
        </div>

        {/* Shown wherever the registry pins a country's doc, which today is
            Malaysia sharing the document /checkout/ already reads. */}
        {sharesExistingDoc && (
          <div className="alert alert-warning py-2">
            <Info size={18} />
            <div className="text-sm">
              <b>{active.label} shares its config with the current checkout page.</b>{" "}
              <code className="px-1">{docPath}</code> is the same document{" "}
              <code className="px-1">/checkout/</code> and the old{" "}
              <a className="link" href="/admin/checkout?tab=offer" target="_blank" rel="noopener noreferrer">
                /admin/checkout
              </a>{" "}
              use, so publishing changes both — deliberately, so the two can
              never show different Malaysian prices. Note the old page charges
              the <b>sale price</b> and ignores the compare price, so lowering
              the sale price here lowers what <code className="px-1">/checkout/</code>{" "}
              charges as soon as you publish.
            </div>
          </div>
        )}

        {/* ---- Price (per country) ---- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Compare price</span>
              <span className="label-text-alt opacity-60">struck through — blank = none</span>
            </label>
            <label className="input input-bordered flex items-center gap-2">
              <span className="opacity-60">{active.currency}</span>
              <input
                className="grow"
                inputMode="decimal"
                value={activePrice.compareAmount}
                onChange={(e) => setPrice(active.code, { compareAmount: e.target.value })}
                placeholder={minorToInput(188600, active)}
                aria-label={`Compare price in ${active.currency}`}
              />
            </label>
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Sale price</span>
              <span className="label-text-alt opacity-60">what gets charged</span>
            </label>
            <label className="input input-bordered flex items-center gap-2">
              <span className="opacity-60">{active.currency}</span>
              <input
                className="grow"
                inputMode="decimal"
                value={activePrice.amount}
                onChange={(e) => setPrice(active.code, { amount: e.target.value })}
                placeholder={minorToInput(125700, active)}
                aria-label={`Sale price in ${active.currency}`}
              />
            </label>
          </div>
        </div>

        {/* Live, and to one decimal: Australia is 32.2% where the rest are
            ~33.3%, and that difference is a decision somebody made. */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {inverted ? (
            <span className="text-error font-semibold">
              The compare price is below the sale price.
            </span>
          ) : pct !== null ? (
            <>
              <span className="badge badge-primary badge-lg">{pct.toFixed(1)}% off</span>
              <span className="opacity-70">
                {formatAmount(compareMinor, active)} → {formatAmount(amountMinor, active)}, saving{" "}
                {formatAmount(compareMinor - amountMinor, active)}
              </span>
            </>
          ) : Number.isInteger(amountMinor) ? (
            <span className="opacity-60">
              {formatAmount(amountMinor, active)} — no compare price, so no discount is shown.
            </span>
          ) : (
            <span className="opacity-60">
              Not priced yet.{" "}
              {existing[active.code]
                ? "This country is published, so it needs a sale price."
                : "This country has no config doc yet; leave it blank and publishing skips it."}
            </span>
          )}
        </div>
        <p className="text-xs opacity-60">
          Type the price the way a buyer reads it — <b>3,998</b>, not{" "}
          <b>{toMinor(3998, active)}</b>. It is converted to the{" "}
          {active.currency} minor units the gateway expects on publish, through
          the same helper the server charges with, so the two cannot disagree.
        </p>

        {/* ---- Promo codes (per country) ---- */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Promo codes — {active.label} only</span>
            <span className="label-text-alt opacity-60">discounts off the sale price</span>
          </label>
          <div className="space-y-2">
            {activePrice.promoCodes.map((p, i) => (
              <div
                key={i}
                className="rounded-lg bg-base-200/60 p-2 sm:rounded-none sm:bg-transparent sm:p-0"
              >
                <div className="mb-2 flex items-center justify-between sm:hidden">
                  <span className="text-xs font-semibold opacity-60">
                    {p.code.trim() ? p.code.trim().toUpperCase() : `Code ${i + 1}`}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1 text-error"
                    onClick={() =>
                      setPrice(active.code, {
                        promoCodes: activePrice.promoCodes.filter((_, j) => j !== i),
                      })
                    }
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>

                <div className="flex gap-2 flex-wrap items-end">
                  <RowField label="Code" className="w-full sm:w-32">
                    <input
                      className="input input-bordered input-sm w-full uppercase"
                      value={p.code}
                      onChange={(e) => updatePromo(active.code, i, { code: e.target.value })}
                      placeholder="CODE"
                      aria-label="Promo code"
                    />
                  </RowField>
                  <RowField label="Discount" className={HALF_ON_MOBILE + " sm:w-auto"}>
                    <select
                      className="select select-bordered select-sm w-full"
                      value={p.type}
                      onChange={(e) => updatePromo(active.code, i, { type: e.target.value })}
                      aria-label="Discount type"
                    >
                      {/* The unit follows the country, so the same dropdown
                          reads "SGD off" on the Singapore tab. */}
                      <option value="flat">{active.currency} off</option>
                      <option value="percent">% off</option>
                    </select>
                  </RowField>
                  <RowField label="Amount" className={HALF_ON_MOBILE + " sm:w-24"}>
                    <input
                      className="input input-bordered input-sm w-full"
                      inputMode="decimal"
                      value={p.value}
                      onChange={(e) => updatePromo(active.code, i, { value: e.target.value })}
                      placeholder={p.type === "percent" ? "10" : minorToInput(50000, active)}
                      aria-label="Discount amount"
                    />
                  </RowField>
                  <RowField label="Expires" className="w-full sm:w-auto">
                    <input
                      type="date"
                      className="input input-bordered input-sm w-full"
                      value={p.expires}
                      onChange={(e) => updatePromo(active.code, i, { expires: e.target.value })}
                      aria-label="Expiry date"
                    />
                  </RowField>
                  <button
                    type="button"
                    className="hidden sm:inline-flex btn btn-xs btn-ghost"
                    onClick={() =>
                      setPrice(active.code, {
                        promoCodes: activePrice.promoCodes.filter((_, j) => j !== i),
                      })
                    }
                    aria-label="Remove code"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            className="btn btn-sm btn-ghost self-start mt-2"
            onClick={() =>
              setPrice(active.code, {
                promoCodes: [
                  ...activePrice.promoCodes,
                  { code: "", type: "flat", value: "", expires: "" },
                ],
              })
            }
          >
            + Add code
          </button>
          <p className="text-xs opacity-60 mt-1">
            Codes are per country — a code added here works on {active.label}{" "}
            and nowhere else, which is the point: a flat{" "}
            {active.currency} discount means nothing against another market’s
            currency. Redemption limits aren’t enforced yet; a code stays valid
            until its expiry date.
          </p>
        </div>

        {/* ---- Shared content ---- */}
        <div className="divider text-sm font-semibold opacity-70">
          Shared content — published to every country
        </div>
        <p className="-mt-2 text-xs opacity-60">
          Everything below is one set of fields for all{" "}
          {COUNTRIES.length} countries. Bonus values are USD figures that are the
          same in every market, so they are not duplicated per country — a copy
          per tab would only be a way for one of them to fall behind. Publishing
          writes this copy to every country’s config along with its own price.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Product title</span>
              <span className="label-text-alt opacity-60">English</span>
            </label>
            <input
              className="input input-bordered"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="JS VIP Community"
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">产品名称</span>
              <span className="label-text-alt opacity-60">blank = use English</span>
            </label>
            <input
              className="input input-bordered"
              value={form.titleZh}
              onChange={(e) => set({ titleZh: e.target.value })}
              placeholder="JS VIP 培训计划"
            />
          </div>
        </div>

        {/* ---- Images ---- */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Hero image</span>
            <span className="label-text-alt opacity-60">
              1920×1080 — compressed automatically
            </span>
          </label>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
          />
          <button
            className="btn btn-outline"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Upload size={16} />
            )}
            Upload image
          </button>

          {form.images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
              {form.images.map((img, i) => (
                <div key={img.url} className="relative">
                  <img
                    src={img.url}
                    alt=""
                    className={`rounded w-full ${i === 0 ? "ring-2 ring-primary" : ""}`}
                  />
                  {i === 0 && (
                    <span className="badge badge-primary badge-sm absolute top-1 left-1">
                      Live
                    </span>
                  )}
                  {img.width && (
                    <span className="badge badge-neutral badge-sm absolute bottom-1 left-1">
                      {img.width}×{img.height}
                    </span>
                  )}
                  <button
                    className="btn btn-sm sm:btn-xs btn-circle absolute top-1 right-1"
                    onClick={() =>
                      set({ images: form.images.filter((x) => x.url !== img.url) })
                    }
                    aria-label="Remove image"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- Bonuses ---- */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Bonuses</span>
            <span className="label-text-alt opacity-60">gold stack under the price</span>
          </label>
          <div className="space-y-2">
            {form.bonuses.map((b, i) => (
              <div
                key={b._id || i}
                // The tint is what makes one bonus readable as one block once
                // the fields stack on a phone.
                className="rounded-lg bg-base-200/60 p-2 sm:rounded-none sm:bg-transparent sm:p-0"
              >
                <div className="mb-2 flex items-center justify-between sm:hidden">
                  <span className="text-xs font-semibold opacity-60">Bonus {i + 1}</span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1 text-error"
                    onClick={() =>
                      set({ bonuses: form.bonuses.filter((_, j) => j !== i) })
                    }
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>

                <div className="flex gap-2 flex-wrap items-end">
                  {/* Arrows only, no drag handle. v1's HTML5 drag is nice on a
                      laptop and does nothing on touch or keyboard, where the
                      arrows are already the whole interaction — so this screen
                      ships the half that works everywhere rather than a second
                      copy of the drag state machine. */}
                  <RowField label="Reorder">
                    <div className="flex shrink-0 flex-row gap-1 sm:flex-col sm:gap-0">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs h-9 min-h-0 px-3 sm:h-4 sm:px-1"
                        onClick={() => moveBonus(i, i - 1)}
                        disabled={i === 0}
                        aria-label={`Move bonus ${i + 1} up`}
                        title="Move up"
                      >
                        <ChevronUp size={12} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs h-9 min-h-0 px-3 sm:h-4 sm:px-1"
                        onClick={() => moveBonus(i, i + 1)}
                        disabled={i === form.bonuses.length - 1}
                        aria-label={`Move bonus ${i + 1} down`}
                        title="Move down"
                      >
                        <ChevronDown size={12} />
                      </button>
                    </div>
                  </RowField>
                  <RowField label="Icon">
                    <IconPicker value={b.icon} onChange={(icon) => updateBonus(i, { icon })} />
                  </RowField>
                  <RowField label="Name (EN)" className="w-full sm:flex-1 sm:min-w-[10rem]">
                    <input
                      className="input input-bordered input-sm w-full"
                      value={b.name}
                      onChange={(e) => updateBonus(i, { name: e.target.value })}
                      placeholder="Jason's daily market analysis"
                      aria-label="Bonus name (English)"
                    />
                  </RowField>
                  <RowField label="名称 (中文)" className="w-full sm:flex-1 sm:min-w-[10rem]">
                    <input
                      className="input input-bordered input-sm w-full"
                      value={b.nameZh}
                      onChange={(e) => updateBonus(i, { nameZh: e.target.value })}
                      placeholder="Jason 每日市场分析"
                      aria-label="Bonus name (Chinese)"
                    />
                  </RowField>
                  <RowField label="Tag (EN)" className={HALF_ON_MOBILE + " sm:w-28"}>
                    <input
                      className="input input-bordered input-sm w-full"
                      value={b.tag}
                      onChange={(e) => updateBonus(i, { tag: e.target.value })}
                      placeholder="LIMITED"
                      aria-label="Tag (English)"
                    />
                  </RowField>
                  <RowField label="标签 (中文)" className={HALF_ON_MOBILE + " sm:w-28"}>
                    <input
                      className="input input-bordered input-sm w-full"
                      value={b.tagZh}
                      onChange={(e) => updateBonus(i, { tagZh: e.target.value })}
                      placeholder="限量"
                      aria-label="Tag (Chinese)"
                    />
                  </RowField>
                  {/* A number or a label. The 中文 box appears only once what's
                      typed stops being a number — there is nothing to translate
                      about "597". */}
                  <RowField label="Value (USD)" className={HALF_ON_MOBILE + " sm:w-24"}>
                    <input
                      className="input input-bordered input-sm w-full"
                      inputMode="decimal"
                      value={b.value}
                      onChange={(e) => updateBonus(i, { value: e.target.value })}
                      placeholder="597 or Priceless"
                      aria-label="Value — a number, or a text label shown as-is"
                    />
                  </RowField>
                  {parseBonusValue(b.value).kind === "label" && (
                    <RowField label="价值 (中文)" className={HALF_ON_MOBILE + " sm:w-24"}>
                      <input
                        className="input input-bordered input-sm w-full"
                        value={b.valueZh || ""}
                        onChange={(e) => updateBonus(i, { valueZh: e.target.value })}
                        placeholder="无价"
                        aria-label="Value label (Chinese)"
                      />
                    </RowField>
                  )}
                  {/* Writes the ~~tildes~~ into both titles at once, so the
                      state can't end up set in one language and not the other.
                      Typing them by hand does the same thing. */}
                  <RowField label="Struck out">
                    <label
                      className="flex h-8 cursor-pointer items-center gap-2 whitespace-nowrap px-1"
                      title={
                        !b.name.trim()
                          ? "Give the bonus a name first"
                          : !canStrikeBonus(b)
                          ? "This name already contains ~~, so the marker can't be read back off it."
                          : "Cross this bonus out — it stays on the page, greyed and struck through, and drops out of the bonus total"
                      }
                    >
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={isBonusStruck(b)}
                        disabled={!b.name.trim() || !canStrikeBonus(b)}
                        onChange={(e) =>
                          updateBonus(i, {
                            name: setBonusTitleStruck(b.name, e.target.checked),
                            nameZh: setBonusTitleStruck(b.nameZh, e.target.checked),
                          })
                        }
                        aria-label="Cross this bonus out"
                      />
                      <span className="hidden text-xs opacity-60 sm:inline">
                        <s>Strike</s>
                      </span>
                    </label>
                  </RowField>
                  <button
                    type="button"
                    className="hidden sm:inline-flex btn btn-xs btn-ghost"
                    onClick={() => set({ bonuses: form.bonuses.filter((_, j) => j !== i) })}
                    aria-label="Remove bonus"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            className="btn btn-sm btn-ghost self-start mt-2"
            onClick={() =>
              set({ bonuses: [...form.bonuses, { ...EMPTY_BONUS, _id: nextBonusId() }] })
            }
          >
            + Add bonus
          </button>
          <p className="text-xs opacity-60 mt-1">
            Rows publish in the order shown. Leave a Chinese field blank and the
            English shows instead. <b>Value</b> takes a number or a word: a
            number shows as a price (“597” → <b>$597</b>) and counts toward the
            total, anything else shows exactly as typed and is left out of it.
            Values are USD everywhere — they are not converted per country, which
            is why they aren’t edited per country either.
          </p>
        </div>

        {/* ---- Seats ---- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Seats counter</span>
              <span className="label-text-alt opacity-60">blank = hidden</span>
            </label>
            <input
              className="input input-bordered"
              inputMode="numeric"
              value={form.seatsStart}
              onChange={(e) => set({ seatsStart: e.target.value })}
              placeholder="25"
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Never drops below</span>
            </label>
            <input
              className="input input-bordered"
              inputMode="numeric"
              value={form.seatsFloor}
              onChange={(e) => set({ seatsFloor: e.target.value })}
            />
          </div>
        </div>

        {/* ---- Preview ---- */}
        <Preview
          form={form}
          country={active}
          amountMinor={amountMinor}
          compareMinor={inverted ? null : compareMinor}
          pct={inverted ? null : pct}
        />
      </div>
    </div>
  );
}

/* How the offer will actually read once published, in the active country's
   currency. The gradient and colours are copied from .bonus-row in
   public/checkout/styles.css — this is a preview, not the live markup, so if
   you change one, change the other. Worth the duplication: the team edits this
   minutes before a webinar closes, and "publish and go look" is a two-minute
   round trip.

   Copied rather than imported from checkout-editor.jsx, which doesn't export
   it. When /checkout/ is retired that file goes and this becomes the only
   copy. */
const GOLD =
  "linear-gradient(112deg,#A9822A 0%,#D9B24C 12%,#F6E19B 26%,#FFF8DC 38%," +
  "#EBCB6B 50%,#C9A02F 64%,#E9CE79 78%,#FFF3BF 90%,#C79E2C 100%)";

/* Both languages of every fixed string the bake script writes, in the same
   shape so the two can be diffed by eye. Admin copy falls back to English when
   its 中文 twin is blank, matching bilingual() in the bake. */
const PREVIEW_COPY = {
  en: {
    head: "Included when you complete today",
    label: (n) => `Bonus ${n}:`,
    totalLead: "Total bonus value",
    totalTail: "— included at no extra cost",
    off: (p) => `${p}% off`,
  },
  zh: {
    head: "今天完成付款即可获得",
    label: (n) => `赠品 ${n}：`,
    totalLead: "赠品总价值",
    totalTail: "— 免费赠送",
    off: (p) => `立减 ${p}%`,
  },
};

function Preview({ form, country, amountMinor, compareMinor, pct }) {
  // The page ships both languages and the buyer picks; previewing only English
  // hides exactly the mistakes the 中文 fields are there to catch — a missing
  // translation, or one long enough to break the row.
  const [lang, setLang] = useState("en");
  const t = PREVIEW_COPY[lang];
  const rows = form.bonuses.filter((b) => b.name.trim());
  const total = totalBonusValue(rows);
  const title = (lang === "zh" && form.titleZh) || form.title;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-1">
        <div className="text-xs opacity-60">
          Live preview — {country.label} ({country.currency})
        </div>
        <div role="tablist" className="tabs tabs-boxed tabs-xs">
          {[
            ["en", "EN"],
            ["zh", "中文"],
          ].map(([code, label]) => (
            <button
              key={code}
              role="tab"
              type="button"
              className={`tab ${lang === code ? "tab-active" : ""}`}
              aria-selected={lang === code}
              onClick={() => setLang(code)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="rounded-2xl p-3"
        style={{ background: "#142845", border: "2px solid #FFD95A" }}
      >
        {form.images[0] && (
          <img
            src={form.images[0].url}
            alt=""
            className="mb-2 w-full rounded-lg"
            style={{ maxHeight: 160, objectFit: "cover" }}
          />
        )}
        {title && (
          <div className="mb-1 text-sm font-bold" style={{ color: "#fff" }}>
            {title}
          </div>
        )}

        {/* The price row, formatted by the registry's formatAmount — the same
            function the page and the order emails use, so "RM 3,998" here and
            "SGD 1,257" there are not two different opinions about currency
            display. */}
        <div className="mb-2 flex flex-wrap items-baseline gap-2">
          {Number.isInteger(compareMinor) && (
            <span style={{ color: "rgba(255,255,255,.55)", textDecoration: "line-through" }}>
              {formatAmount(compareMinor, country)}
            </span>
          )}
          <span className="text-xl font-extrabold" style={{ color: "#FFD95A" }}>
            {Number.isInteger(amountMinor) ? formatAmount(amountMinor, country) : "—"}
          </span>
          {pct !== null && (
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-extrabold"
              style={{ background: "#C20A17", color: "#fff" }}
            >
              {t.off(pct.toFixed(1))}
            </span>
          )}
        </div>

        {rows.length > 0 && (
          <div
            className="flex items-center gap-2 mb-2 text-sm font-bold"
            style={{ color: "#FFD95A" }}
          >
            🎁 {t.head}
          </div>
        )}
        {rows.map((b, i) => {
          // Mirrors .bonus-row.is-struck on the live page: dimmed, drained of
          // the gold, struck end to end.
          const struck = isBonusStruck(b);
          const strike = struck ? "line-through" : "none";
          return (
            <div
              key={b._id || i}
              className="flex items-center gap-1.5 rounded-lg mb-1.5 px-2 py-2 text-[11px]"
              style={{
                background: GOLD,
                color: "#2A1D00",
                opacity: struck ? 0.45 : 1,
                filter: struck ? "saturate(.25)" : "none",
              }}
            >
              {b.icon && (
                <span style={{ color: "#5A4000", display: "inline-flex" }}>
                  <BonusIcon name={b.icon} size={16} />
                </span>
              )}
              <span className="font-extrabold" style={{ color: "#5A4000", textDecoration: strike }}>
                {t.label(i + 1)}
              </span>
              <span
                className="flex-1 min-w-0 truncate font-semibold"
                style={{ color: "#241900", textDecoration: strike }}
              >
                {(lang === "zh" && bonusTitleText(b.nameZh)) || bonusTitleText(b.name)}
              </span>
              {b.tag && (
                <span
                  className="px-1 py-0.5 rounded text-[8px] font-extrabold whitespace-nowrap"
                  style={{ background: struck ? "#6B6257" : "#C20A17", color: "#fff" }}
                >
                  {(lang === "zh" && b.tagZh) || b.tag}
                </span>
              )}
              {(() => {
                // A figure is language-neutral and ships as one span; a label is
                // admin copy and follows the switch, falling back to English.
                const parsed = parseBonusValue(b.value);
                if (parsed.kind === "none") return null;
                return (
                  <span className="font-extrabold" style={{ color: "#1E1400", textDecoration: strike }}>
                    {parsed.kind === "number"
                      ? formatBonusMoney(parsed.value)
                      : (lang === "zh" && b.valueZh) || parsed.value}
                  </span>
                );
              })()}
            </div>
          );
        })}
        {total > 0 && (
          <p
            className="mt-2 pt-2 text-center text-[11px]"
            style={{
              borderTop: "1px solid rgba(255,217,90,.3)",
              color: "rgba(255,255,255,.85)",
            }}
          >
            {t.totalLead} <b style={{ color: "#FFD95A" }}>{formatBonusMoney(total)}</b>{" "}
            {t.totalTail}
          </p>
        )}
      </div>
      <p className="mt-1 text-xs opacity-60">
        Bonus figures are USD in every market — only the price row above follows
        the country. Day {DAYS.join(" and ")} are separate offers; publishing one
        leaves the other untouched.
      </p>
    </div>
  );
}
